import React, { ReactElement, ReactNode, useEffect } from 'react';
import { withSubComponents } from '@/utils/react';
import Pagination, { usePagination } from './Pagination';
import { SortDirection } from '@/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp, faFileExcel } from '@fortawesome/pro-light-svg-icons';
import { LoadingOverlay, SearchInput } from '@ygtechnologies/bygr-frontend-component-library';
import { Button } from '@ygtechnologies/rubus-ui';
import theme from './DataTable.module.scss';

export const defaultRenderer = ({ value }) => value;
export function createDataTable<T>() {
    interface DataTableState {
        pageSize: number;
        pageNumber: number;
        sortBy: keyof T;
        sortDirection: SortDirection;
        searchText: string;
    }

    interface DataColumnProps<PropName extends keyof T = keyof T> {
        header: ReactNode;
        field: PropName;
        renderer?(params: { row: T; value: T[PropName] }): ReactNode;
        sortable?: boolean;
    }

    interface DataTableProps {
        data: T[];
        state?: DataTableState;
        children: ReactElement<DataColumnProps>[];
        onStateChange?(request: DataTableState): void;
        hasPagination?: boolean;
        totalItemCount?: number;
        className?: string;
        isLoading?: boolean;
        hasSearchField?: boolean;
        exportUrl?: string;
        /** page-level actions (e.g. a create button) rendered on the right side of the toolbar row */
        toolbarActions?: ReactNode;
    }

    const DataTable: React.FC<DataTableProps> = ({
        children,
        data,
        totalItemCount,
        state,
        className,
        onStateChange,
        isLoading = false,
        hasSearchField = false,
        hasPagination = true,
        exportUrl,
        toolbarActions,
    }) => {
        const paginationState = usePagination(totalItemCount, state?.pageSize);

        //TODO: do a better fix for this
        useEffect(() => {
            if (state && state.pageNumber !== paginationState.currentPage) {
                onStateChange!({
                    ...state,
                    pageNumber: paginationState.currentPage,
                });
            }
        }, [state, paginationState, onStateChange]);

        const handleHeaderClick = (columnProps: DataColumnProps) => {
            //TODO: fix parameter typing
            if (!columnProps.sortable || !onStateChange || !state) {
                return;
            }

            if (state.sortBy === columnProps.field) {
                onStateChange({
                    ...state,
                    sortDirection:
                        state.sortDirection === SortDirection.Ascending ? SortDirection.Descending : SortDirection.Ascending,
                });
            } else {
                onStateChange({
                    ...state,
                    sortBy: columnProps.field,
                    sortDirection: SortDirection.Ascending,
                });
            }
        };

        const renderArrows = (sortable: boolean | undefined, columnProps: DataColumnProps) => {
            if (!sortable || !state) {
                return null;
            }

            if (columnProps.field === state.sortBy) {
                return <FontAwesomeIcon icon={state.sortDirection === SortDirection.Ascending ? faAngleUp : faAngleDown} />;
            }
            return <FontAwesomeIcon color="grey" icon={faAngleUp} />;
        };

        const showSearchField = !!(hasSearchField && onStateChange && state);

        return (
            <div className={theme.container}>
                <LoadingOverlay visible={isLoading}>
                    {(showSearchField || exportUrl || toolbarActions) && (
                        <div className={theme.toolbar}>
                            {showSearchField && (
                                <SearchInput
                                    className={theme.searchInput}
                                    searchText={state.searchText}
                                    onSearchTextChange={(v) => {
                                        // Back to the first page — the old page is meaningless for a new search
                                        paginationState.setPage(0);
                                        onStateChange({ ...state, searchText: v, pageNumber: 0 });
                                    }}
                                />
                            )}
                            {exportUrl && (
                                <Button as="a" size="sm" variant="tertiary" icon={faFileExcel} href={exportUrl}>
                                    Export
                                </Button>
                            )}
                            {toolbarActions && <div className={theme.toolbarActions}>{toolbarActions}</div>}
                        </div>
                    )}

                    <table className={className}>
                        <thead>
                            <tr>
                                {React.Children.map(children, (c, index) => (
                                    <th key={index} onClick={() => handleHeaderClick(c.props)}>
                                        {c.props.header} {renderArrows(c.props.sortable, c.props)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        {!isLoading && data.length === 0 && (
                            <tbody>
                                <tr>
                                    <th style={{ textAlign: 'center' }} colSpan={React.Children.count(children)}>
                                        No data
                                    </th>
                                </tr>
                            </tbody>
                        )}
                        <tbody>
                            {data.map((row, index) => (
                                <tr key={index}>
                                    {React.Children.map(children, (c, index) => (
                                        <td key={index}>
                                            {(c.props.renderer ?? defaultRenderer)({ value: row[c.props.field], row })}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {hasPagination && <Pagination value={paginationState} />}
                </LoadingOverlay>
            </div>
        );
    };

    function CustomColumn<PropName extends keyof T>(_props: DataColumnProps<PropName>) {
        return null;
    }

    function getInitialState(
        defaultSortColumn: keyof T,
        defaultSortDirection = SortDirection.Ascending,
        pageSize = 10
    ): DataTableState {
        return {
            pageNumber: 0,
            pageSize: pageSize,
            sortBy: defaultSortColumn,
            sortDirection: defaultSortDirection,
            searchText: '',
        };
    }

    return withSubComponents(DataTable, {
        Column: CustomColumn,
        getInitialState,
    });
}
